import { fromNullable } from 'maybeasy';
import { observer } from 'mobx-react';
import { Search, Users, Plus } from 'lucide-react';
import { currentUserStore } from '../../../../CurrentUserStore';
import * as React from 'react';
import { L, T, TranslationsContext, translation } from '../../../../Translations';
import { FormStore } from './Stores/FormStore';
import { License } from './Stores/Types';
import { Collection, UserGroupWithAssociations } from './Stores/ParticipantOptionsStore/Types';

interface GroupSelectionSectionProps {
  formStore: FormStore;
  groups: UserGroupWithAssociations[];
  licenses: License[];
  handleGroupToggle: (groupId: number, checked: boolean) => void;
  handleCreateNewGroup: () => void;
  onCreateFromSearch: (groupName: string) => void;
}

function GroupSelectionSection({
  formStore,
  groups,
  licenses,
  handleGroupToggle,
  handleCreateNewGroup,
  onCreateFromSearch,
}: GroupSelectionSectionProps) {
  const ts = React.useContext(TranslationsContext);

  // Get current user's organization ID
  const currentOrgId =
    currentUserStore.currentUserState.kind === 'identified'
      ? currentUserStore.currentUserState.currentUserResource.payload.organizationId
      : null;

  const activeGroups = groups
    .filter(
      (g) =>
        fromNullable(g.status)
          .map((s) => s === 'active')
          .getOrElse(() => true) &&
        (currentOrgId === null || g.organizationId === currentOrgId),
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  // Get newly created groups from participantStore using stored ID mappings
  const newGroups = Array.from(formStore.newGroupIdMap.entries())
    .map(([id, name]) => ({
      id,
      name,
      isNew: true,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Combine new groups first (at top), then existing groups
  const allGroups = [...newGroups, ...activeGroups];

  const filteredGroups = allGroups.filter((g) =>
    g.name.toLowerCase().includes(formStore.groupSearchQuery.toLowerCase()),
  );

  return (
    <div className="bg-card border border-border rounded-lg p-8">
      <div className="flex items-start gap-4 mb-6">
        <div
          className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 text-lg"
          style={{ fontWeight: 'var(--font-weight-semibold)' }}
        >
          2
        </div>
        <div className="flex-1">
          <h2 className="text-xl mb-1" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
            <T kind="Groups" />
          </h2>
          <p className="text-muted-foreground">
            <T kind="Optionally add to groups for collection access, license allocation, and reporting." />
          </p>
        </div>
      </div>

      <div className="relative mb-4">
        <input
          type="text"
          placeholder={translation('Search groups...')(ts)}
          value={formStore.groupSearchQuery}
          onChange={(e) => formStore.updateGroupSearchQuery(e.target.value)}
          className="w-full px-4 py-2 pl-10 border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          data-intercom-target="search-groups-input"
        />
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      </div>

      {/* Create New Group Button */}
      {!formStore.showCreateGroupInput && (
        <div className="mb-4">
          <button
            onClick={() => formStore.updateShowCreateGroupInput(true)}
            className="w-full px-4 py-2 border-2 border-dashed border-border rounded-md hover:border-primary hover:bg-primary/5 transition-colors flex items-center justify-center gap-2 text-muted-foreground hover:text-primary"
            style={{ fontWeight: 'var(--font-weight-medium)' }}
            data-intercom-target="create-new-group-button"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            <T kind="Create New Group" />
          </button>
        </div>
      )}

      {/* Create New Group Inline */}
      {formStore.showCreateGroupInput && (
        <div className="mb-4 p-4 bg-muted/30 border border-border rounded-lg">
          <label
            htmlFor="new-group-name-input"
            className="block mb-2"
            style={{ fontWeight: 'var(--font-weight-medium)' }}
          >
            <T kind="New group name" />
          </label>
          <div className="flex gap-2">
            <input
              id="new-group-name-input"
              type="text"
              value={formStore.newGroupName}
              onChange={(e) => formStore.updateNewGroupName(e.target.value)}
              placeholder={translation('Enter group name')(ts)}
              className="flex-1 px-3 py-2 border border-border rounded-md bg-background focus:ring-2 focus:ring-ring focus:outline-none"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && formStore.newGroupName.trim()) {
                  handleCreateNewGroup();
                } else if (e.key === 'Escape') {
                  formStore.updateShowCreateGroupInput(false);
                  formStore.updateNewGroupName('');
                }
              }}
            />
            <button
              onClick={handleCreateNewGroup}
              disabled={!formStore.newGroupName.trim()}
              className="px-4 py-2 bg-accent text-accent-foreground rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontWeight: 'var(--font-weight-medium)' }}
              data-intercom-target="create-new-group-submit"
            >
              <T kind="Create" />
            </button>
            <button
              onClick={() => {
                formStore.updateShowCreateGroupInput(false);
                formStore.updateNewGroupName('');
              }}
              className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors"
              data-intercom-target="create-new-group-cancel"
            >
              <T kind="Cancel" />
            </button>
          </div>
        </div>
      )}

      {/* Selection Summary */}
      <div className="mb-3 flex items-center justify-between">
        <span className="text-muted-foreground" style={{ fontSize: 'var(--text-sm)' }}>
          {formStore.groupSearchQuery && (
            <>
              <T kind="Showing {{count}} •" count={filteredGroups.length} />{' '}
            </>
          )}
          <T
            kind="{{selectedCount}} of {{totalCount}} selected"
            selectedCount={formStore.selectedGroupIds.length}
            totalCount={activeGroups.length + formStore.newGroupIdMap.size}
          />
        </span>
        {formStore.selectedGroupIds.length > 0 && (
          <button
            onClick={() => {
              formStore.updateSelectedGroupIds([]);
              formStore.updateSelectedLicenseIds(
                formStore.selectedLicenseIds.filter(
                  (id) => !formStore.groupSuggestedLicenseIds.has(id),
                ),
              );
              formStore.updateGroupSuggestedLicenseIds(new Set());
            }}
            className="text-primary hover:opacity-80 transition-opacity"
            style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
            }}
            data-intercom-target="clear-all-groups-button"
          >
            <T kind="Clear all" />
          </button>
        )}
      </div>

      <div className="space-y-3 max-h-80 overflow-y-auto">
        {allGroups.length === 0 && !formStore.groupSearchQuery ? (
          <div className="p-8 text-center border border-dashed border-border rounded-lg bg-muted/10">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-40" />
            <h3 className="mb-2">
              <T kind="No groups yet" />
            </h3>
            <p className="text-muted-foreground mb-4" style={{ fontSize: 'var(--text-sm)' }}>
              <T kind="Groups let you organize participants for collection access, license allocation, and reporting." />
            </p>
            <button
              onClick={() => formStore.updateShowCreateGroupInput(true)}
              className="px-4 py-2 border-2 border-dashed border-border rounded-md hover:border-primary hover:bg-primary/5 transition-colors inline-flex items-center gap-2 text-muted-foreground hover:text-primary"
              style={{ fontWeight: 'var(--font-weight-medium)' }}
              data-intercom-target="empty-state-create-first-group-button"
            >
              <Plus className="w-4 h-4" />
              <T kind="Create Your First Group" />
            </button>
          </div>
        ) : filteredGroups.length > 0 ? (
          filteredGroups.map((group) => {
            const isNewGroup = 'isNew' in group && group.isNew;
            const memberCount = !isNewGroup && 'memberCount' in group ? group.memberCount : 0;
            const associatedLicenseIds =
              !isNewGroup && 'associatedLicenseIds' in group ? group.associatedLicenseIds : [];
            const hasAssociatedLicenses = associatedLicenseIds.length > 0;
            const collections = !isNewGroup && 'collections' in group ? group.collections : [];
            const hasCollections = collections.length > 0;

            const isSelected = formStore.selectedGroupIds.includes(group.id);

            return (
              <label
                key={group.id}
                className={`flex items-start gap-3 p-4 border rounded-md cursor-pointer transition-colors ${
                  isSelected
                    ? 'border-accent bg-card'
                    : 'border-border bg-muted/10 hover:border-border'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => handleGroupToggle(group.id, e.target.checked)}
                  className="w-5 h-5 mt-0.5 rounded cursor-pointer text-primary border-border focus:ring-primary"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="text-base"
                      style={{ fontWeight: 'var(--font-weight-semibold)' }}
                    >
                      {group.name}
                    </span>
                    {isNewGroup && (
                      <span className="px-2 py-0.5 bg-accent/10 text-accent rounded text-xs border border-accent/20">
                        <T kind="New" />
                      </span>
                    )}
                    {!isNewGroup && hasAssociatedLicenses && (
                      <div
                        className="relative"
                        onMouseEnter={() => formStore.updateHoveredGroupTooltip(group.id)}
                        onMouseLeave={() => formStore.updateHoveredGroupTooltip(null)}
                      >
                        <span className="px-2 py-0.5 rounded bg-primary/10 text-primary text-xs cursor-help">
                          <T kind="{{count}} license" count={associatedLicenseIds.length} />
                        </span>
                        {formStore.hoveredGroupTooltip === group.id && (
                          <div className="absolute top-full left-0 mt-2 px-4 py-3 bg-card text-foreground rounded shadow-lg border border-border w-80 z-50">
                            <p
                              className="mb-1"
                              style={{
                                fontWeight: 'var(--font-weight-semibold)',
                              }}
                            >
                              <T
                                kind="Associated Licenses ({{count}})"
                                count={associatedLicenseIds.length}
                              />
                            </p>
                            <div className="space-y-2">
                              {associatedLicenseIds.map((licenseId) => {
                                const license = licenses.find((l) => l.id === licenseId);
                                if (!license) {
                                  return null;
                                }
                                const licenseName = license.licenseName.getOrElse(() => '');
                                return (
                                  <div key={licenseId} className="text-sm">
                                    <div
                                      style={{
                                        fontWeight: 'var(--font-weight-medium)',
                                      }}
                                    >
                                      {license.licenseTypeName}
                                    </div>
                                    <div className="text-muted-foreground">
                                      {licenseName && `${licenseName} (`}
                                      <L
                                        localizeable={license.startOn}
                                        format="short-month-day-year"
                                      />
                                      {' - '}
                                      <L
                                        localizeable={license.endOn}
                                        format="short-month-day-year"
                                      />
                                      {licenseName && ')'}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="absolute bottom-full left-4 w-2 h-2 bg-card border-l border-t border-border rotate-45 mb-[-4px]" />
                          </div>
                        )}
                      </div>
                    )}
                    {hasCollections && (
                      <div
                        className="relative"
                        onMouseEnter={() => formStore.updateHoveredCollectionTooltip(group.id)}
                        onMouseLeave={() => formStore.updateHoveredCollectionTooltip(null)}
                      >
                        <span className="px-2 py-0.5 rounded bg-accent/10 text-accent text-xs cursor-help">
                          <T kind="{{count}} collection" count={collections.length} />
                        </span>
                        {formStore.hoveredCollectionTooltip === group.id && (
                          <div className="absolute top-full left-0 mt-2 px-4 py-3 bg-card text-foreground rounded shadow-lg border border-border w-80 z-50">
                            <p
                              className="mb-1"
                              style={{
                                fontWeight: 'var(--font-weight-semibold)',
                              }}
                            >
                              <T
                                kind="Associated Collections ({{count}})"
                                count={collections.length}
                              />
                            </p>
                            <div className="space-y-2">
                              {collections.map((collection: Collection) => (
                                <div key={collection.id} className="text-sm">
                                  <div
                                    style={{
                                      fontWeight: 'var(--font-weight-medium)',
                                    }}
                                  >
                                    {collection.name}
                                  </div>
                                  <div className="text-muted-foreground">
                                    <T
                                      kind="{{count}} course"
                                      count={collection.learningDesignCount}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="absolute bottom-full left-4 w-2 h-2 bg-card border-l border-t border-border rotate-45 mb-[-4px]" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {!isNewGroup && (
                      <>
                        <div className="flex items-center gap-1">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                            />
                          </svg>
                          <span>
                            <T kind="{{count}} member" count={memberCount} />
                          </span>
                        </div>
                      </>
                    )}
                    {isNewGroup && (
                      <>
                        <span className="text-xs">
                          <T kind="Will be created on submit" />
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </label>
            );
          })
        ) : (
          <div className="p-8 text-center border border-dashed border-border rounded-lg bg-muted/10">
            <Search className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-40" />
            <p className="text-muted-foreground mb-4" style={{ fontSize: 'var(--text-sm)' }}>
              <T kind='No groups found matching "{{query}}"' query={formStore.groupSearchQuery} />
            </p>
            <button
              onClick={() => {
                const groupName = formStore.groupSearchQuery.trim();
                if (!groupName) return;

                onCreateFromSearch(groupName);

                const newGroupId = -Date.now();
                const newMap = new Map(formStore.newGroupIdMap);
                newMap.set(newGroupId, groupName);
                formStore.updateNewGroupIdMap(newMap);
                formStore.updateSelectedGroupIds([...formStore.selectedGroupIds, newGroupId]);
                formStore.updateGroupSearchQuery('');
              }}
              className="px-4 py-2 bg-accent text-accent-foreground rounded-md hover:opacity-90 transition-opacity inline-flex items-center gap-2"
              style={{ fontWeight: 'var(--font-weight-medium)' }}
              data-intercom-target="empty-state-create-group-button"
            >
              <Plus className="w-4 h-4" />
              <T kind='Create "{{groupName}}"' groupName={formStore.groupSearchQuery} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default observer(GroupSelectionSection);
